import { diffOrderedSets, type OrderedSetDiff } from './diffOrderedSets';

/**
 * Merge 2 sets of diffs that are based on the same ancestor.
 * It returns a unique set of diffs that can be applied to the ancestor to produce the same result as applying the 2 sets of diffs.
 */
export function mergeOrderedSetDiffs(
    base: OrderedSetDiff[],
    head: OrderedSetDiff[]
): OrderedSetDiff[] {
    const diffs = [base, head].map(sortByNewIndexDesc);
    // @ts-ignore
    return unionDiffs(diffs[0], diffs[1]);
}

function sortByNewIndexDesc(diff: OrderedSetDiff[]) {
    return diff.sort((a, b) => {
        if (a[0] === null) {
            return 1;
        }
        if (b[0] === null) {
            return -1;
        }

        return a[0] < b[0] ? 1 : -1;
    });
}

function unionDiffs(diff1: OrderedSetDiff[], diff2: OrderedSetDiff[]): OrderedSetDiff[] {
    const result: OrderedSetDiff[] = [];

    while (diff1.length && diff2.length) {
        const entries = [diff1, diff2].map((each) => each.pop() as OrderedSetDiff);
        const newIndexes = entries.map((each) => each?.[0] ?? null);
        if (newIndexes[0] === newIndexes[1]) {
            // @ts-ignore
            result.push([entries[0][0], mergeEntry(entries[0][1], entries[1][1])]);
        } else if (!entries[0] || (newIndexes[0] || 0) > (newIndexes[1] || 0)) {
            if (entries[0]) {
                diff1.push(entries[0]);
            }
            // @ts-ignore
            result.push(entries[1]);
        } else {
            if (entries[1]) {
                diff2.push(entries[1]);
            }
            result.push(entries[0]);
        }
    }
    return result.concat(diff1, diff2);
}

function mergeEntry(updates1: OrderedSetDiff[1], updates2: OrderedSetDiff[1]) {
    const updates2Map: Record<string, OrderedSetDiff[1][number]> = {};
    const arrays = [updates1, updates2].map((each) => {
        return each.map((update) => {
            const serialized = update.insert !== undefined ? `i${update.insert}` : update.move;
            updates2Map[serialized] = update;
            return serialized;
        });
    });

    // @ts-expect-error
    const updateDiff = diffOrderedSets(arrays[0], arrays[1]);

    const result = updates1.map((each) => {
        return [each];
    });
    updateDiff.forEach((each) => {
        const index2 = each[0];
        each[1].forEach((diffEntry) => {
            if (index2 !== null) {
                // Ensure an array exists at the index
                result[index2] = result[index2] ?? [];
                if (diffEntry.insert !== undefined) {
                    // @ts-ignore
                    result[index2].push(updates2Map[diffEntry.insert]);
                } else if (diffEntry.move !== undefined) {
                    // @ts-ignore
                    result[index2].push(updates2Map[diffEntry.move]);
                }
            }
        });
    });

    return result.flat();
}
